import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Field } from "./Field";

const REGIONS = [
  { value: "dutch-caribbean",   label: "Dutch Caribbean" },
  { value: "english-caribbean", label: "English Caribbean" },
  { value: "spanish-caribbean", label: "Spanish Caribbean" },
  { value: "french-caribbean",  label: "French Caribbean" },
  { value: "other",             label: "Other" },
];

const LANGS = [
  { value: "papiamento", label: "Papiamento" },
  { value: "dutch",      label: "Dutch" },
  { value: "english",    label: "English" },
  { value: "spanish",    label: "Spanish" },
  { value: "french",     label: "French" },
];

const emptyIsland = {
  name: "", slug: "", flag_emoji: "", language_primary: "english",
  region: "other", accent_color: "#0AADA0", is_active: false,
  sort_order: 99, meta_title: "", meta_description: "",
};

const slugifyIsland = (s) =>
  (s || "").toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-")
    .replace(/-+/g, "-").replace(/^-|-$/g, "");

const IslandEditModal = ({ mode, initial, onClose, onSaved }) => {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(mode === "edit");

  const setName = (name) => {
    setF((p) => ({
      ...p,
      name,
      slug: slugTouched ? p.slug : slugifyIsland(name),
    }));
  };
  const setSlug = (v) => {
    setSlugTouched(true);
    setF((p) => ({ ...p, slug: slugifyIsland(v) }));
  };
  const setField = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (mode === "create") {
        await api.post("/admin/islands", f);
        toast.success("Island created");
      } else {
        await api.patch(`/admin/islands/${initial.slug}`, f);
        toast.success("Island updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const rem60 = 60 - (f.meta_title || "").length;
  const rem160 = 160 - (f.meta_description || "").length;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="island-edit-modal">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E5] px-6 py-4 flex items-center justify-between">
          <h3 className="font-medium text-[#1A1A1A]">
            {mode === "create" ? "Add New Island" : `Edit ${initial.name}`}
          </h3>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#1A1A1A]">
            <span className="text-xl">×</span>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Island name" value={f.name} onChange={setName}
                 testid="island-input-name" placeholder="e.g. Barbados" />
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">
              Slug {mode === "edit" && <span className="text-[#E63946]">(cannot change)</span>}
            </label>
            <input type="text" value={f.slug}
                   onChange={(e) => setSlug(e.target.value)}
                   disabled={mode === "edit"}
                   className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm focus:outline-none focus:border-[#0AADA0] disabled:bg-[#FAFAFA] disabled:text-[#6B7280]"
                   data-testid="island-input-slug" />
            <div className="text-[11px] text-[#6B7280] mt-1">Page: islandsdaily.com/{f.slug || "…"}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">Flag emoji</label>
              <div className="flex items-center gap-2">
                <input type="text" value={f.flag_emoji}
                       onChange={(e) => setField("flag_emoji", e.target.value)}
                       placeholder="🇧🇧"
                       className="flex-1 px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm focus:outline-none focus:border-[#0AADA0]"
                       data-testid="island-input-flag" />
                <span className="text-2xl" aria-hidden="true">{f.flag_emoji}</span>
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">Accent color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={f.accent_color}
                       onChange={(e) => setField("accent_color", e.target.value.toUpperCase())}
                       className="h-9 w-14 rounded border border-[#E5E5E5]"
                       data-testid="island-input-color" />
                <input type="text" value={f.accent_color}
                       onChange={(e) => setField("accent_color", e.target.value)}
                       className="flex-1 px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm font-mono focus:outline-none focus:border-[#0AADA0]" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">Region</label>
              <select value={f.region} onChange={(e) => setField("region", e.target.value)}
                      data-testid="island-input-region"
                      className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm focus:outline-none focus:border-[#0AADA0] bg-white">
                {REGIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">Primary language</label>
              <select value={f.language_primary} onChange={(e) => setField("language_primary", e.target.value)}
                      data-testid="island-input-lang"
                      className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm focus:outline-none focus:border-[#0AADA0] bg-white">
                {LANGS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">
              Meta title <span className={`ml-1 ${rem60 < 0 ? "text-[#E63946]" : "text-[#6B7280]"}`}>{rem60} chars left</span>
            </label>
            <input type="text" value={f.meta_title}
                   onChange={(e) => setField("meta_title", e.target.value)}
                   maxLength={60}
                   placeholder={`${f.name || "Island"} News in English — IslandsDaily`}
                   className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm focus:outline-none focus:border-[#0AADA0]"
                   data-testid="island-input-metatitle" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">
              Meta description <span className={`ml-1 ${rem160 < 0 ? "text-[#E63946]" : "text-[#6B7280]"}`}>{rem160} chars left</span>
            </label>
            <textarea rows={2} value={f.meta_description}
                      onChange={(e) => setField("meta_description", e.target.value)}
                      maxLength={160}
                      className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm focus:outline-none focus:border-[#0AADA0]"
                      data-testid="island-input-metadesc" />
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">Sort order</label>
              <input type="number" value={f.sort_order}
                     onChange={(e) => setField("sort_order", parseInt(e.target.value, 10) || 99)}
                     className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm focus:outline-none focus:border-[#0AADA0]"
                     data-testid="island-input-order" />
            </div>
            <label className="flex items-center gap-2 text-sm text-[#1A1A1A] py-2">
              <input type="checkbox" checked={f.is_active}
                     onChange={(e) => setField("is_active", e.target.checked)}
                     data-testid="island-input-active"
                     className="w-4 h-4" />
              <span>Active — appears in site nav</span>
            </label>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#E5E5E5] px-6 py-3 flex items-center gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm text-[#6B7280] hover:bg-[#FAFAFA]" data-testid="island-cancel-btn">Cancel</button>
          <button onClick={save} disabled={saving || !f.name || !f.slug}
                  className="px-4 py-2 rounded-full bg-[#0AADA0] text-white text-sm font-medium hover:bg-[#0AADA0]/90 disabled:opacity-50"
                  data-testid="island-save-btn">
            {saving ? "Saving…" : "Save island"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const IslandsTab = () => {
  const [islands, setIslands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/islands");
      setIslands(r.data);
    } catch { toast.error("Failed to load islands"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const move = async (slug, dir) => {
    const idx = islands.findIndex((i) => i.slug === slug);
    if (idx < 0) return;
    const nextIdx = idx + dir;
    if (nextIdx < 0 || nextIdx >= islands.length) return;
    const reordered = [...islands];
    [reordered[idx], reordered[nextIdx]] = [reordered[nextIdx], reordered[idx]];
    setIslands(reordered);
    try {
      await api.post("/admin/islands/reorder", { slugs: reordered.map((i) => i.slug) });
    } catch { toast.error("Reorder failed"); load(); }
  };

  const toggleActive = async (island) => {
    try {
      await api.patch(`/admin/islands/${island.slug}`, {
        ...island, is_active: !island.is_active,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  };

  const del = async (island) => {
    if (!window.confirm(`Delete ${island.name}? This is irreversible.`)) return;
    try {
      await api.delete(`/admin/islands/${island.slug}`);
      toast.success("Island deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-islands-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-[13px] uppercase tracking-wider text-[#6B7280]">
          Islands ({islands.length})
        </h2>
        <button
          onClick={() => setEditing({ mode: "create", data: emptyIsland })}
          className="px-4 py-2 rounded-full bg-[#0AADA0] text-white text-sm font-medium hover:bg-[#0AADA0]/90"
          data-testid="add-island-btn"
        >
          + Add New Island
        </button>
      </div>

      {loading ? (
        <div className="card-soft p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {islands.map((i, idx) => (
            <div key={i.slug} className="card-soft p-4 flex flex-col gap-3"
                 style={{ borderTop: `3px solid ${i.accent_color}` }}
                 data-testid={`island-card-${i.slug}`}>
              <div className="flex items-start gap-3">
                <span className="text-3xl leading-none" aria-hidden="true">{i.flag_emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#1A1A1A]">{i.name}</div>
                  <div className="text-xs text-[#6B7280] mt-0.5">
                    <span className="px-1.5 py-0.5 rounded bg-[#FAFAFA]">{i.region}</span>
                    <span className="ml-2">/{i.slug}</span>
                  </div>
                </div>
                <button
                  onClick={() => toggleActive(i)}
                  data-testid={`island-toggle-${i.slug}`}
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    i.is_active ? "bg-[#3BB273]" : "bg-[#E5E5E5]"
                  }`}
                  aria-label={i.is_active ? "Deactivate" : "Activate"}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    i.is_active ? "translate-x-5" : "translate-x-0.5"
                  }`} />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center text-[11px] text-[#6B7280]">
                <div>
                  <div className="text-[#1A1A1A] font-medium text-sm">{i.article_count}</div>
                  <div className="uppercase tracking-wide">Articles</div>
                </div>
                <div>
                  <div className="text-[#1A1A1A] font-medium text-sm">{i.articles_today}</div>
                  <div className="uppercase tracking-wide">Today</div>
                </div>
                <div>
                  <div className="text-[#1A1A1A] font-medium text-sm">#{i.sort_order}</div>
                  <div className="uppercase tracking-wide">Order</div>
                </div>
              </div>

              <div className="flex items-center gap-1 pt-1">
                <button
                  onClick={() => move(i.slug, -1)}
                  disabled={idx === 0}
                  className="p-1.5 rounded hover:bg-[#FAFAFA] disabled:opacity-30 text-[#6B7280]"
                  data-testid={`island-up-${i.slug}`}
                  aria-label="Move up"
                >↑</button>
                <button
                  onClick={() => move(i.slug, 1)}
                  disabled={idx === islands.length - 1}
                  className="p-1.5 rounded hover:bg-[#FAFAFA] disabled:opacity-30 text-[#6B7280]"
                  data-testid={`island-down-${i.slug}`}
                  aria-label="Move down"
                >↓</button>
                <button
                  onClick={() => setEditing({ mode: "edit", data: i })}
                  className="ml-auto text-xs text-[#0AADA0] hover:underline"
                  data-testid={`island-edit-${i.slug}`}
                >Edit</button>
                <button
                  onClick={() => del(i)}
                  className="p-1.5 rounded text-[#6B7280] hover:bg-[#E63946]/10 hover:text-[#E63946]"
                  data-testid={`island-delete-${i.slug}`}
                  aria-label="Delete"
                ><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <IslandEditModal
          mode={editing.mode}
          initial={editing.data}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};
